import React from 'react';
import { Box } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { mix, sbg, SBG_HEX } from '../../theme/sbgTokens';

/*
 * Team colors on the floor plan.
 *
 * Every team keeps the identity color the backend assigns
 * (backend/src/constants/floorplan.ts) — Team Kyle stays blue everywhere —
 * and every view renders it the same theme-aware way:
 *
 *   fill   a soft tint of the team color over the surface
 *   edge   the team color pulled toward the ink color (darker in light mode,
 *          lighter in dark mode), so the outline always clears the floor
 *   strip  a 3px bar of the full team color
 *
 * Text never takes the team color: it stays on text tokens, which clear AA on
 * any of these tints in both themes. The tint strengths are the --fp-team-*
 * properties in index.css (stronger in dark mode, where tints read weaker).
 * A desk carries its own color in an inline --fp-team custom property.
 */

const HEX = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;

/** The team color if it is a plain hex value, else null (never pass data into CSS unchecked). */
export const safeTeamColor = (color?: string | null): string | null => (color && HEX.test(color.trim()) ? color.trim() : null);

/** Inline style that sets the team color for an element and its children. */
export const teamColorStyle = (color?: string | null): React.CSSProperties | undefined => {
  const safe = safeTeamColor(color);
  return safe ? ({ '--fp-team': safe } as React.CSSProperties) : undefined;
};

/** CSS values that read the element's --fp-team. */
export const TEAM = {
  solid: 'var(--fp-team)',
  fill: 'color-mix(in srgb, var(--fp-team) var(--fp-team-tint), var(--sbg-bg-surface))',
  fillStrong: 'color-mix(in srgb, var(--fp-team) var(--fp-team-tint-strong), var(--sbg-bg-surface))',
  edge: 'color-mix(in srgb, var(--fp-team) var(--fp-team-edge), var(--sbg-text-primary))',
  strip: 'inset 0 3px 0 0 var(--fp-team)',
} as const;

/** A small round team marker for lists, menus and detail rows. */
export const TeamDot: React.FC<{ color?: string | null; size?: number; sx?: SxProps<Theme> }> = ({ color, size = 8, sx }) => {
  const safe = safeTeamColor(color);
  return (
    <Box
      component="span"
      aria-hidden="true"
      style={teamColorStyle(safe)}
      sx={{
        display: 'inline-block',
        width: size,
        height: size,
        flex: 'none',
        borderRadius: '50%',
        bgcolor: safe ? TEAM.solid : 'transparent',
        // A hairline keeps pale team colors (yellows, mints) visible on white.
        boxShadow: `inset 0 0 0 1px ${safe ? mix(sbg.text.primary, 16) : sbg.border.strong}`,
        ...sx,
      }}
    />
  );
};

/** A desk-shaped swatch: the same fill, edge and strip as a team's desks. */
export const TeamDeskSwatch: React.FC<{ color?: string | null; strong?: boolean; sx?: SxProps<Theme> }> = ({ color, strong = false, sx }) => {
  const safe = safeTeamColor(color);
  return (
    <Box
      component="span"
      aria-hidden="true"
      style={teamColorStyle(safe)}
      sx={{
        display: 'inline-block',
        width: 16,
        height: 12,
        flex: 'none',
        boxSizing: 'border-box',
        borderRadius: '3px',
        ...(safe
          ? { bgcolor: strong ? TEAM.fillStrong : TEAM.fill, border: `${strong ? 2 : 1}px solid ${TEAM.edge}`, boxShadow: 'inset 0 2px 0 0 var(--fp-team)' }
          : { bgcolor: sbg.bg.surface, border: `1px solid ${sbg.border.strong}` }),
        ...sx,
      }}
    />
  );
};

/* ---- Print (standalone document: no CSS variables, light theme only) ---- */

const toRgb = (hex: string) => {
  const value = hex.replace('#', '');
  const full = value.length === 3 ? value.split('').map((c) => c + c).join('') : value;
  const n = parseInt(full, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};

const blend = (hex: string, base: string, share: number) => {
  const a = toRgb(hex);
  const b = toRgb(base);
  const channel = (i: number) => Math.round(a[i] * share + b[i] * (1 - share)).toString(16).padStart(2, '0');
  return `#${channel(0)}${channel(1)}${channel(2)}`;
};

/** Literal light-theme hex values for a team's desks on the printed plan (same recipe as the screen). */
export const printTeamColors = (color?: string | null) => {
  const safe = safeTeamColor(color);
  if (!safe) return null;
  return {
    solid: safe,
    fill: blend(safe, SBG_HEX.white, 0.22),
    edge: blend(safe, SBG_HEX.navy, 0.62),
  };
};
